import React, { FormEvent, useEffect, useState } from "react";
import classNames from "classnames";
import {
    Alert,
    Button,
    FormControl,
    Grid,
    LinearProgress,
    Link,
    Paper,
    TextField,
    Typography,
    Divider,
    Box
} from "@mui/material";
import { withStyles } from "@mui/styles";
import styles from "./SignUpForm-jss";
import { live4tvUserService } from "./live4tvUserService";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { Auth, Profile } from "./userDTO";
import { setIsLoggedIn, setUserProfile } from "./userActions";
import useCrashReport from "../../packages/useCrashReport/useCrashReport";
import { useNavigate } from "react-router-dom";
import useGTM from "../../packages/useGTM/useGTM";
import GoogleSignInButton from "./GoogleSignInButton";
import FacebookSignInButton from "./FacebookSignInButton";

type Properties = {
    classes: any,
    auth: Auth,
    profile: Profile,
    setUserProfile: Function,
    setIsLoggedIn: Function,
    onSuccess?: () => void,
}
function MagicLinkSignupPage(props: Properties) {
    const {
        classes,
        auth,
        profile,
        setUserProfile,
        setIsLoggedIn,
        onSuccess,
    } = props;

    const [isLoading, setIsLoading] = useState(false);
    const [isGoogleLoading, setIsGoogleLoading] = useState(false);
    const [isFacebookLoading, setIsFacebookLoading] = useState(false);
    const [formError, setFormError] = useState('');
    const [username, setUsername] = useState('');
    const [firstName, setFirstName] = useState('');
    const [success, setSuccess] = useState('');

    const { t } = useTranslation();

    const crashReporter = useCrashReport();
    const gtm = useGTM({});
    const navigate = useNavigate();

    useEffect(() => {
        console.debug('MagicLinkSignupPage');
    }, []);

    useEffect(() => {
        console.debug('signedIn', auth.isLoggedIn);
        if (auth.isLoggedIn === true) {
            crashReporter.setUser(profile.code);
            if (onSuccess) {
                onSuccess();
            } else {
                navigate('/events');
            }
        }
    }, [auth.isLoggedIn]);


    const handleSignUp = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            const breakDownName = firstName.split(' ');
            const fName = breakDownName[0];
            const lName = breakDownName.slice(1).join(' ');

            const response = await live4tvUserService.signUpMagicLink({
                firstName: fName,
                lastNames: lName,
                username: username,
                segment: 'na',
                persona: '',
                whatsapp: '',
            }, onSuccess ? true : undefined);
            console.debug('handleSignUp().signup', response);

            if (response.data && (response.data as any).token) {
                const token = (response.data as any).token;
                const roles = (response.data as any).roles;

                const user = await live4tvUserService.getUser(token);
                console.log('handleSignUp().user', user);

                const profile: Profile = {
                    firstname: user.firstname,
                    lastnames: user.lastnames,
                    code: user.code,
                    email: user.email,
                    username: user.username,
                    token: token,
                    roles: roles,
                }

                setUserProfile(profile);
                setIsLoggedIn(true);
                crashReporter.setUser(profile.code);
                gtm.trackEvent('login', {
                    user: {
                        id: profile.code
                    },
                });

                if (onSuccess) {
                    onSuccess();
                } else {
                    navigate('/events');
                }
                return;
            }

            setSuccess(t('Login link generated successfully. Please check your email for the link. It may take a few minutes to arrive.'));

        } catch (e: any) {
            console.debug('handleLogin().error', e);
            let errors = '';
            if (e.errors !== undefined) {
                e.errors.forEach((error: string) => errors += t(error) + '\n');
            } else {
                errors = t('Something didn\'t work well. Please, try again');
            }
            setFormError(errors);
        }

        setIsLoading(false);
    }

    async function handleGoogleSignUp(credential: string) {
        setIsGoogleLoading(true);
        setFormError('');
        setSuccess('');

        try {
            const response = await live4tvUserService.signUpWithGoogle(credential);
            console.debug('handleGoogleSignUp().signup', response);

            const user = await live4tvUserService.getUser(response.token);
            console.log('handleGoogleSignUp().user', user);

            const profile: Profile = {
                firstname: user.firstname,
                lastnames: user.lastnames,
                code: user.code,
                email: user.email,
                username: user.username,
                token: response.token,
                roles: response.roles,
            }

            setUserProfile(profile);
            setIsLoggedIn(true);
            crashReporter.setUser(profile.code);
            gtm.trackEvent('signup', {
                method: 'google',
                user: {
                    id: profile.code
                },
            });
        } catch (error: any) {
            console.debug('handleGoogleSignUp().error', error);
            let errors = '';
            if (error.errors !== undefined) {
                error.errors.forEach((err: string) => errors += t(err) + '\n');
            } else {
                errors = t('Google authentication failed. Please try again.');
            }
            setFormError(errors);
        }

        setIsGoogleLoading(false);
    }

    function handleGoogleError(error: string) {
        setFormError(error);
    }

    async function handleFacebookSignUp(accessToken: string) {
        setIsFacebookLoading(true);
        setFormError('');
        setSuccess('');

        try {
            const response = await live4tvUserService.signUpWithFacebook(accessToken);
            console.debug('handleFacebookSignUp().signup', response);

            const user = await live4tvUserService.getUser(response.token);
            console.log('handleFacebookSignUp().user', user);

            const profile: Profile = {
                firstname: user.firstname,
                lastnames: user.lastnames,
                code: user.code,
                email: user.email,
                username: user.username,
                token: response.token,
                roles: response.roles,
            }

            setUserProfile(profile);
            setIsLoggedIn(true);
            crashReporter.setUser(profile.code);
            gtm.trackEvent('signup', {
                method: 'facebook',
                user: {
                    id: profile.code
                },
            });
        } catch (error: any) {
            console.debug('handleFacebookSignUp().error', error);
            let errors = '';
            if (error.errors !== undefined) {
                error.errors.forEach((err: string) => errors += t(err) + '\n');
            } else {
                errors = t('Facebook authentication failed. Please try again.');
            }
            setFormError(errors);
        }

        setIsFacebookLoading(false);
    }

    function handleFacebookError(error: string) {
        setFormError(error);
    }

    return (
        <Grid container className={classNames(classes.root)} direction="row" alignItems="center" justifyContent="center">
            <Grid item xs={10} md={8} lg={6} container direction="column" spacing={2}>
                <Grid item>
                    <Paper className={classNames(classes.paperForm)} elevation={0} square>
                        {success ? <Alert severity="success" className={classNames(classes.alert)}>{success}</Alert> : (
                            <div className={classNames(classes.form)}>
                                {(isLoading || isGoogleLoading || isFacebookLoading) ? <LinearProgress className={classNames(classes.progressBar)} /> : (
                                    <div>
                                        <Typography component="h1" variant="h5">
                                            {t("Signup")}
                                        </Typography>
                                        {formError ? <Alert severity="error"
                                            className={classNames(classes.alert)}>{formError}</Alert> : ''}

                                        <form onSubmit={handleSignUp}>
                                            <FormControl fullWidth className={classNames(classes.textField)}>
                                                <TextField
                                                    fullWidth
                                                    autoFocus
                                                    margin="dense"
                                                    id="firstname"
                                                    label={t("Enter with your name")}
                                                    type="text"
                                                    value={firstName}
                                                    onChange={(e) => setFirstName(e.target.value)}
                                                    required
                                                />
                                            </FormControl>
                                            <FormControl fullWidth className={classNames(classes.textField)}>
                                                <TextField
                                                    margin="dense"
                                                    id="login"
                                                    label={t("Enter with an email")}
                                                    type="email"
                                                    fullWidth
                                                    value={username}
                                                    onChange={(e) => setUsername(e.target.value)}
                                                    required
                                                />
                                            </FormControl>

                                            <FormControl fullWidth></FormControl>
                                            <Button fullWidth size="large" type="submit" variant="contained" color="primary"
                                                className={classNames(classes.submit)}
                                                disabled={isLoading || isGoogleLoading || isFacebookLoading}
                                                sx={{ mb: 0 }}>
                                                {t("Signup")}
                                            </Button>
                                        </form>

                                        <Box sx={{ mt: 5, mb: 1 }}>&nbsp;</Box>

                                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                            <Divider sx={{ flexGrow: 1 }} />
                                            <Typography variant="body2" sx={{ mx: 2, color: 'text.secondary' }}>
                                                {t("OR")}
                                            </Typography>
                                            <Divider sx={{ flexGrow: 1 }} />
                                        </Box>

                                        <Box>&nbsp;</Box>

                                        <Box sx={{ mt: 0, mb: 2, width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
                                            <GoogleSignInButton
                                                onSuccess={handleGoogleSignUp}
                                                onError={handleGoogleError}
                                                disabled={isLoading || isGoogleLoading || isFacebookLoading}
                                                text={t("Continue with Google")}
                                                className={classNames(classes.textField)}
                                            />
                                            <FacebookSignInButton
                                                onSuccess={handleFacebookSignUp}
                                                onError={handleFacebookError}
                                                disabled={isLoading || isGoogleLoading || isFacebookLoading}
                                                text={t("Continue with Facebook")}
                                                className={classNames(classes.textField)}
                                            />
                                        </Box>
                                    </div>
                                )}

                                <div className={classNames(classes.serviceLinks)}>
                                    <Link href="#" onClick={(e) => {
                                        e.preventDefault();
                                        window.open('https://www.live4.tv/termos-de-servico', "_blank", "noreferrer");
                                    }} color="textPrimary">
                                        {t('Terms of Service')}
                                    </Link>

                                    <Link href="#" onClick={(e) => {
                                        e.preventDefault();
                                        window.open('https://www.live4.tv/politica-de-privacidade', "_blank", "noreferrer");
                                    }} color="textPrimary">
                                        {t("Privacy Policy")}
                                    </Link>
                                </div>
                            </div>
                        )}
                    </Paper>
                </Grid>

                <Grid item>
                    <Link href="#login" color="textPrimary">
                        {t("Do you already have an account? Click here to Login")}
                    </Link>
                </Grid>
            </Grid>
        </Grid>
    );
}

const mapStateToProps = function (state: any) {
    return {
        auth: state.auth,
        profile: state.user.profile
    }
}

const mapDispatchToProps = function (dispatch: any) {
    return {
        setIsLoggedIn: (signedIn: boolean) => dispatch(setIsLoggedIn(signedIn)),
        setUserProfile: (profile: Profile) => dispatch(setUserProfile(profile)),
    }
}

export default withStyles(styles)(connect(mapStateToProps, mapDispatchToProps)(MagicLinkSignupPage));
